import React, {
    useState,
    useContext,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import { Form } from 'formik';
import * as fns from 'date-fns';
import * as R from 'ramda';

import {
    AlertBanner,
    Row,
    Col,
    Flex,
    SearchSelect as NativeSearchSelect,
    FormGroup,
    FormGroupHint,
    FormGroupLabel,
} from '@app/ui';

import Button from '@app/ui/sports-components/Button';
import TargetHelperModal from './TargetHelperModal';

import SearchSelect from '@app/ui/sports-components/SearchSelect/index.js';
import {
    getSelectedValues,
    toSearchSelectOptions,
    toSelectedObject,
} from '@app/utils/multiSelectValue';
import sportsApi from '../utils/sportsApi';
import fetchSponsors from '../actions/settings/fetchSponsors';
import fetchPlacements from '../actions/settings/fetchPlacements';
import {
    getPlatformTypeOptions,
    getMetricOptions,
    getMetricTooltip,
} from './options';
import { durationId, smvId, impressionsId } from '../constants/pacingMetrics';
import getIds from '../utils/getIdsFromSelectedObject';
import { CreatePacingContext } from './CreatePacingContext';
import useFormatDatePicker from '../components/hooks/useFormatDatePicker';
import MvcDatePicker from '../components/common/DatePicker/MvcDatePicker';
import {
    BROADCAST_IMPRESSIONS_FULL_MVC,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
} from '../constants/config';
import { Rightsholder } from '../types/api';
import { useNavigate } from 'react-router-dom';
import { useRightsholderAttribution } from '../components/hooks/useRightsholderAttribution';

type CreatePacingFormProps = {
    values: any;
    errors: any;
    status: any;
    setFieldValue: any;
    client: any;
    filters: any;
};

const CreatePacingForm = ({
    values,
    errors,
    status,
    setFieldValue,
    client,
    filters,
}: CreatePacingFormProps) => {
    const { t } = useTranslation('tools');
    const navigate = useNavigate();
    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const useRhAttribution = rightsholderAttribution === 'league';
    const hasRhAttributionPermission = useMemo(
        () => client?.permissions?.includes(EDGE_RIGHTSHOLDER_ATTRIBUTION),
        [client],
    );
    const methodologyValue = useRhAttribution ? 'league' : 'team';
    const hasBroadcastImpressionsPermission = useMemo(
        () => client.permissions.includes(BROADCAST_IMPRESSIONS_FULL_MVC),
        [client],
    );

    const { setAreAllPlacementsSelected, areAllPlacementsSelected } =
        useContext(CreatePacingContext);
    const { setUserActivities } = useContext(CreatePacingContext);

    const { formatDate } = useFormatDatePicker();

    const [rhLeagueId, setRhLeagueId] = useState(undefined);
    const [sponsorsOptions, setSponsorsOptions] = useState([]);
    const [sponsorsLoading, setSponsorsLoading] = useState(false);
    const [placementOptions, setPlacementOptions] = useState([]);
    const [placementLoading, setPlacementLoading] = useState(false);
    const [startMonth, setStartMonth] = useState(undefined);
    const [endMonth, setEndMonth] = useState(undefined);
    const [isOpenHelperModal, setOpenHelperModal] = useState(false);
    const previousMethodologyRef = useRef<string>(methodologyValue);
    const suppressDependentFetchRef = useRef<boolean>(false);

    const setStartMonthUnique = date => setStartMonth(date);
    const setEndMonthUnique = date => setEndMonth(date);

    const rightsholdersById = useMemo(
        () => filters.rightsholders.list,
        [filters],
    );

    const {
        platformPostTypePairs: { list: platformList },
    } = filters;

    const platformTypeOptions = getPlatformTypeOptions(
        Object.keys(platformList),
    );

    const rightsholderOptions = useMemo(() => {
        if (filters.isLoading) return [];
        return Object.entries(rightsholdersById).map(
            ([id, rh]: [string, Rightsholder]) => {
                return { name: rh.name, value: id };
            },
        );
    }, [filters.isLoading, rightsholdersById]);

    const set = field => value => setFieldValue(field, value, false);

    useEffect(() => {
        if (rhLeagueId) {
            updateMostRecentDate(rhLeagueId);
        }
        setFieldValue('startDate', null, false);
        setFieldValue('endDate', null, false);
    }, [rhLeagueId]);

    useEffect(() => {
        set('metric')(null);
    }, [values.platformTypes]);

    useEffect(() => {
        set('targetValue')('');
    }, [values.metric]);

    useEffect(() => {
        // Reset create-goal form when attribution methodology changes
        // so we don't call sponsor/placement endpoints with stale RH ids.
        if (previousMethodologyRef.current !== methodologyValue) {
            suppressDependentFetchRef.current = true;
            previousMethodologyRef.current = methodologyValue;
            setFieldValue('name', '', false);
            setFieldValue('rightsholder', undefined, false);
            setFieldValue('sponsor', undefined, false);
            setFieldValue('placements', {}, false);
            setFieldValue('platformTypes', {}, false);
            setFieldValue('startDate', undefined, false);
            setFieldValue('endDate', undefined, false);
            setFieldValue('metric', undefined, false);
            setFieldValue('targetValue', undefined, false);
            setRhLeagueId(undefined);
            setSponsorsOptions([]);
            setPlacementOptions([]);
            setAreAllPlacementsSelected(true);
            setUserActivities([]);
        }
    }, [
        methodologyValue,
        setAreAllPlacementsSelected,
        setFieldValue,
        setUserActivities,
    ]);

    useEffect(() => {
        if (!suppressDependentFetchRef.current) return;
        if (!values.rightsholder && !values.sponsor) {
            suppressDependentFetchRef.current = false;
        }
    }, [values.rightsholder, values.sponsor]);

    useEffect(() => {
        (async () => {
            if (suppressDependentFetchRef.current) return;
            if (!values.rightsholder) return;
            const isValidRightsholder = rightsholderOptions.some(
                option => `${option.value}` === `${values.rightsholder}`,
            );
            if (!isValidRightsholder) {
                setSponsorsOptions([]);
                setPlacementOptions([]);
                setFieldValue('sponsor', undefined, false);
                setFieldValue('placements', {}, false);
                return;
            }

            setSponsorsLoading(true);
            const sponsorsArray = await fetchSponsors({
                clientId: client.id,
                rightsholderId: values.rightsholder,
                useRightsholderAttribution: useRhAttribution,
            });

            setSponsorsOptions(
                R.uniqBy(
                    R.path(['value']),
                    sponsorsArray.map(({ name, value }) => {
                        return {
                            value,
                            name,
                        };
                    }),
                ),
            );

            setSponsorsLoading(false);
        })();
    }, [values.rightsholder, useRhAttribution, rightsholderOptions]);

    useEffect(() => {
        (async () => {
            if (suppressDependentFetchRef.current) return;
            if (!values.sponsor) return;
            const isValidSponsor = sponsorsOptions.some(
                option => `${option.value}` === `${values.sponsor}`,
            );
            if (!isValidSponsor) {
                setPlacementOptions([]);
                setFieldValue('placements', {}, false);
                return;
            }

            setPlacementLoading(true);
            const placementsArray = await fetchPlacements({
                clientId: client.id,
                rightsholderId: values.rightsholder,
                sponsorId: values.sponsor,
                useRightsholderAttribution: useRhAttribution,
            });

            setPlacementOptions(
                R.uniqBy(
                    R.path(['value']),
                    placementsArray.map(({ name, value }) => {
                        return {
                            value,
                            name,
                        };
                    }),
                ),
            );

            setPlacementLoading(false);
        })();
    }, [values.sponsor, useRhAttribution, sponsorsOptions]);

    useEffect(() => {
        setAreAllPlacementsSelected(
            placementOptions.length === getIds(values.placements).length ||
                getIds(values.placements).length === 0,
        );
    }, [placementOptions, values.placements]);

    const { isSubmitting } = status;

    const navigatePacing = useCallback(() => {
        if (!isSubmitting) {
            const confirmation = confirm(
                t('pacing.modals.unsavedChanges.message'),
            );
            if (!confirmation) return;
        }
        navigate(`/c/${client.id}/pacing`);
    }, [navigate, client, isSubmitting, t]);

    const hasError = field => !!errors[field];
    const isDisabled = field => {
        if (isSubmitting) {
            return true;
        }

        switch (field) {
            case 'rightsholder':
                return rightsholderOptions.length === 0;

            case 'sponsor':
                return (
                    !values.rightsholder ||
                    sponsorsOptions.length === 0 ||
                    sponsorsLoading
                );

            case 'placement':
                return (
                    !values.sponsor ||
                    placementOptions.length === 0 ||
                    placementLoading
                );

            case 'platformTypes':
                return platformTypeOptions.length === 0;

            case 'metric':
                return metricOptions.length === 0;

            case 'date':
                return !values.rightsholder;

            case 'targetValue':
                return !values.metric;

            default:
                return false;
        }
    };

    const updateMostRecentDate = async leagueId => {
        const leagueSeasons: any[] = leagueId
            ? [
                  await sportsApi
                      .url('/league-seasons')
                      .query({ leagueId })
                      .get()
                      .json(),
              ]
            : [];

        const leaguesSeasonsDates = leagueSeasons.flatMap(seasons =>
            seasons.map(season => season),
        );

        // Filter only seasons with end dates in future
        const seasonsDatesInFuture = leaguesSeasonsDates.filter(league =>
            fns.isAfter(league.endDate, new Date()),
        );

        // Find the most recent start date
        const recentSeasonInLeagues =
            (seasonsDatesInFuture.length &&
                seasonsDatesInFuture.reduce((mostRecent, current) => {
                    if (fns.isAfter(current.startDate, mostRecent.startDate)) {
                        return current;
                    }
                    return mostRecent;
                })) ||
            null;

        const newMostRecentSeason =
            (recentSeasonInLeagues && {
                startDate: formatDate(recentSeasonInLeagues.startDate),
                endDate: formatDate(recentSeasonInLeagues.endDate),
            }) ||
            null;

        // set startDate & endDate
        const startDate =
            newMostRecentSeason && new Date(newMostRecentSeason.startDate);
        const endDate =
            newMostRecentSeason && new Date(newMostRecentSeason.endDate);

        // No start date or end date found in the future
        if (!startDate && !endDate) {
            setFieldValue('startDate', '', false);
            setFieldValue('endDate', '', false);
            return;
        }

        // set start and end date as found values
        setFieldValue('startDate', startDate, false);
        setFieldValue('endDate', endDate, false);
    };

    const setRightsholder = value => {
        const prevValue = values.rightsholder;
        setFieldValue('rightsholder', value, false);

        const leagueId =
            rightsholdersById && rightsholdersById[value]
                ? rightsholdersById[value].leagueId
                : null;

        setRhLeagueId(leagueId);

        if (value !== prevValue) {
            setSponsorsOptions([]);
            setPlacementOptions([]);
            set('sponsor')('');
            set('placements')({});
        }
    };

    const setSponsor = value => {
        const prevValue = values.sponsor;
        setFieldValue('sponsor', value, false);

        if (value !== prevValue) {
            setPlacementOptions([]);
            set('placements')({});
        }
    };

    const metricOptions = useMemo(
        () =>
            getMetricOptions(
                getIds(values.platformTypes),
                hasBroadcastImpressionsPermission,
            ),
        [values.platformTypes],
    );
    const metricTooltip = useMemo(
        () => getMetricTooltip(values.metric, getIds(values.platformTypes)),
        [values.metric, values.platformTypes],
    );
    const BroadcastImpressionTooltip = t(
        'pacing.createGoal.helpers.broadcastInfo',
    );

    const shouldDisplayBroadcastTooltip = useMemo(
        () =>
            getIds(values.platformTypes).includes('2') &&
            values.metric === impressionsId,
        [values.platformTypes, values.metric],
    );

    const changeName = e => {
        const name = e.target.value;
        set('name')(name);
    };

    const changeTargetValue = e => {
        const value = parseInt(e.target.value);
        set('targetValue')(value);
    };

    const openHelperModal = () => setOpenHelperModal(true);
    const closeHelperModal = () => setOpenHelperModal(false);

    const metricValueString = useMemo(() => {
        if (!values.metric || !metricOptions) return '';
        if (values.metric === durationId) return 'seconds';
        if (values.metric === smvId) return client.currency.code;
        const metricOption = metricOptions.find(o => o.value === values.metric);

        if (!metricOption) return '';

        return metricOption.name.toLocaleLowerCase();
    }, [values.metric, metricOptions]);

    const dateDiff = useMemo(
        () =>
            parseInt(
                (
                    (values.endDate - values.startDate) /
                    (3600 * 1000 * 24)
                ).toString(),
            ) + 1,
        [values.endDate, values.startDate],
    );

    return (
        <>
            <Form>
                <Row gutter={16} className="wizard-pagination -m-t-3">
                    <Col md={24}>
                        <h1 data-test="title" className="gds-text--header-sm">
                            {t('pacing.createGoal.title')}
                        </h1>
                    </Col>
                </Row>
                {hasRhAttributionPermission && (
                    <>
                        <Row gutter={16} className="-m-t-2">
                            <Col md={24}>
                                <hr className="pacing-methodology-divider" />
                            </Col>
                        </Row>
                        <Row gutter={16} className="-m-t-2">
                            <Col md={24}>
                                <AlertBanner
                                    className="pacing-methodology-banner"
                                    data-test="methodology-banner"
                                    description={t(
                                        'pacing.createGoal.methodology.description',
                                    )}
                                    message={t(
                                        methodologyValue === 'league'
                                            ? 'pacing.createGoal.methodology.leagueGoalMessage'
                                            : 'pacing.createGoal.methodology.teamGoalMessage',
                                    )}
                                    severity="warning"
                                />
                            </Col>
                        </Row>
                    </>
                )}
                <Row gutter={16} className="wizard-pagination -m-t-3">
                    <Flex vertical className="gds-layout__container">
                        <Row gutter={16} className="-m-t-2">
                            <Col md={2}>
                                <div
                                    data-test="title"
                                    className="pacing-step-number"
                                >
                                    1
                                </div>
                            </Col>
                            <Col md={22}>
                                <Flex
                                    vertical
                                    className="gds-layout__container"
                                >
                                    <Row gutter={16}>
                                        <Col md={24}>
                                            <p>
                                                {t(
                                                    'pacing.createGoal.steps.partnership',
                                                )}
                                            </p>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled(
                                                    'rightsholder',
                                                )}
                                                data-test="rightsholder-dropdown"
                                                context={
                                                    hasError('rightsholders')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.rightsholder',
                                                    )}
                                                />
                                                <SearchSelect
                                                    name="rightsholder"
                                                    size="sm"
                                                    placeholder={t(
                                                        'pacing.createGoal.placeholders.rightsholder',
                                                    )}
                                                    options={
                                                        rightsholderOptions
                                                    }
                                                    onChange={setRightsholder}
                                                    value={values.rightsholder}
                                                    hideClearButton
                                                />

                                                {hasError('rightsholder') && (
                                                    <FormGroupHint>
                                                        {errors.rightsholder}
                                                    </FormGroupHint>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled('sponsor')}
                                                data-test="sponsor-dropdown"
                                                context={
                                                    hasError('sponsor')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.sponsor',
                                                    )}
                                                />
                                                <SearchSelect
                                                    name="sponsors"
                                                    size="sm"
                                                    placeholder={t(
                                                        'pacing.createGoal.placeholders.sponsor',
                                                    )}
                                                    options={sponsorsOptions}
                                                    onChange={setSponsor}
                                                    value={values.sponsor}
                                                    hideClearButton
                                                />

                                                {hasError('sponsor') && (
                                                    <FormGroupHint>
                                                        {errors.sponsor}
                                                    </FormGroupHint>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled(
                                                    'placement',
                                                )}
                                                data-test="placement-dropdown"
                                                context={
                                                    hasError('placement')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.placements',
                                                    )}
                                                />
                                                <NativeSearchSelect
                                                    mode="multiple"
                                                    name="placements"
                                                    size="sm"
                                                    placeholder={t(
                                                        'pacing.createGoal.placeholders.placements',
                                                    )}
                                                    options={toSearchSelectOptions(
                                                        placementOptions,
                                                        'name',
                                                    )}
                                                    onChange={value =>
                                                        set('placements')(
                                                            toSelectedObject(
                                                                value,
                                                            ),
                                                        )
                                                    }
                                                    value={getSelectedValues(
                                                        placementOptions,
                                                        values.placements,
                                                    )}
                                                    hideClearButton
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Flex>
                            </Col>
                        </Row>
                        <Row gutter={16} className="-m-t-2">
                            <Col md={2}>
                                <div
                                    data-test="title"
                                    className="pacing-step-number"
                                >
                                    2
                                </div>
                            </Col>
                            <Col md={22}>
                                <Flex
                                    vertical
                                    className="gds-layout__container"
                                >
                                    <Row gutter={16}>
                                        <Col md={24}>
                                            <p>
                                                {t(
                                                    'pacing.createGoal.steps.timeframe',
                                                )}
                                            </p>
                                        </Col>
                                        <Col md={8}>
                                            <div className="gds-flex__item gds-flex__item--grow-0">
                                                <FormGroup
                                                    layout="vertical"
                                                    disabled={isDisabled(
                                                        'date',
                                                    )}
                                                    context={
                                                        errors.startDate
                                                            ? 'danger'
                                                            : 'primary'
                                                    }
                                                >
                                                    <FormGroupLabel
                                                        text={t(
                                                            'pacing.createGoal.fields.startDate',
                                                        )}
                                                    />

                                                    <MvcDatePicker
                                                        value={values.startDate}
                                                        monthInCalendar={
                                                            startMonth
                                                        }
                                                        maxDate={values.endDate}
                                                        minDate={
                                                            new Date(
                                                                new Date().setFullYear(
                                                                    new Date().getFullYear() -
                                                                        1,
                                                                ),
                                                            )
                                                        }
                                                        onDateSelectionChange={set(
                                                            'startDate',
                                                        )}
                                                        onMonthInCalendarChange={
                                                            setStartMonthUnique
                                                        }
                                                        inputProps={{
                                                            className:
                                                                'gds-form-group__text-input',
                                                        }}
                                                        className="gds-card gds-flex-grid__item--full-width "
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    />

                                                    {errors.startDate && (
                                                        <FormGroupHint>
                                                            {errors.startDate}
                                                        </FormGroupHint>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </Col>
                                        <Col md={8}>
                                            <div className="gds-flex__item gds-flex__item--grow-0">
                                                <FormGroup
                                                    layout="vertical"
                                                    disabled={isDisabled(
                                                        'date',
                                                    )}
                                                    context={
                                                        errors.endDate
                                                            ? 'danger'
                                                            : 'primary'
                                                    }
                                                >
                                                    <FormGroupLabel
                                                        text={t(
                                                            'pacing.createGoal.fields.endDate',
                                                        )}
                                                    />

                                                    <MvcDatePicker
                                                        value={values.endDate}
                                                        monthInCalendar={
                                                            endMonth
                                                        }
                                                        onDateSelectionChange={set(
                                                            'endDate',
                                                        )}
                                                        onMonthInCalendarChange={
                                                            setEndMonthUnique
                                                        }
                                                        limitYear={currentYear =>
                                                            currentYear + 1
                                                        }
                                                        inputProps={{
                                                            className:
                                                                'gds-form-group__text-input',
                                                        }}
                                                        maxDate={
                                                            values.startDate &&
                                                            new Date(
                                                                new Date(
                                                                    values.startDate,
                                                                ).setFullYear(
                                                                    new Date(
                                                                        values.startDate,
                                                                    ).getFullYear() +
                                                                        2,
                                                                ),
                                                            )
                                                        }
                                                        minDate={
                                                            values.startDate
                                                        }
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    />

                                                    {errors.endDate && (
                                                        <FormGroupHint>
                                                            {errors.endDate}
                                                        </FormGroupHint>
                                                    )}

                                                    {values.endDate && (
                                                        <p className="gds-text--body-sm -float-right">
                                                            {t(
                                                                'pacing.createGoal.helpers.totalDays',
                                                                { dateDiff },
                                                            )}
                                                        </p>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </Col>
                                    </Row>
                                </Flex>
                            </Col>
                        </Row>
                        <Row gutter={16} className="-m-t-2">
                            <Col md={2}>
                                <div
                                    data-test="title"
                                    className="pacing-step-number"
                                >
                                    3
                                </div>
                            </Col>
                            <Col md={22}>
                                <Flex
                                    vertical
                                    className="gds-layout__container"
                                >
                                    <Row gutter={16}>
                                        <Col md={24}>
                                            <p>
                                                {t(
                                                    'pacing.createGoal.steps.target',
                                                )}
                                            </p>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled(
                                                    'platformTypes',
                                                )}
                                                data-test="platformTypes-dropdown"
                                                context={
                                                    hasError('platformTypes')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.platformTypes',
                                                    )}
                                                />

                                                <NativeSearchSelect
                                                    mode="multiple"
                                                    name="platformTypes"
                                                    size="sm"
                                                    placeholder={t(
                                                        'pacing.createGoal.placeholders.platformTypes',
                                                    )}
                                                    options={toSearchSelectOptions(
                                                        platformTypeOptions,
                                                    )}
                                                    onChange={value =>
                                                        set('platformTypes')(
                                                            toSelectedObject(
                                                                value,
                                                            ),
                                                        )
                                                    }
                                                    value={getSelectedValues(
                                                        platformTypeOptions,
                                                        values.platformTypes,
                                                    )}
                                                    hideClearButton
                                                />

                                                {hasError('platformTypes') && (
                                                    <FormGroupHint>
                                                        {errors.platformTypes}
                                                    </FormGroupHint>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled('metric')}
                                                data-test="metric-dropdown"
                                                context={
                                                    hasError('metric')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.goalMetric',
                                                    )}
                                                />
                                                <SearchSelect
                                                    name="metric"
                                                    size="sm"
                                                    placeholder={t(
                                                        'pacing.createGoal.placeholders.goalMetric',
                                                    )}
                                                    options={metricOptions}
                                                    onChange={set('metric')}
                                                    value={values.metric}
                                                    hideClearButton
                                                />
                                                {hasError('platformTypes') && (
                                                    <FormGroupHint>
                                                        {errors.metric}
                                                    </FormGroupHint>
                                                )}
                                            </FormGroup>
                                            {metricTooltip && (
                                                <p className="gds-text--body-sm">
                                                    {metricTooltip}
                                                </p>
                                            )}
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup
                                                layout="vertical"
                                                disabled={isDisabled(
                                                    'targetValue',
                                                )}
                                                data-test="targetValue-dropdown"
                                                context={
                                                    hasError('targetValue')
                                                        ? 'danger'
                                                        : 'primary'
                                                }
                                            >
                                                <FormGroupLabel
                                                    text={t(
                                                        'pacing.createGoal.fields.targetValue',
                                                    )}
                                                />
                                                <div className="-target-helper-container -p-l-2 gds-text--body-sm">
                                                    <a
                                                        className="-target-helper"
                                                        onClick={
                                                            openHelperModal
                                                        }
                                                    >
                                                        {t(
                                                            'pacing.createGoal.helpers.setTarget',
                                                        )}
                                                    </a>
                                                </div>
                                                <div className="gds-form-group__input-group -m-b-1">
                                                    <input
                                                        className="gds-form-group__text-input gds-form-group__text-input--sm"
                                                        type="number"
                                                        style={{
                                                            width: '50%',
                                                        }}
                                                        name={`input_start`}
                                                        onChange={
                                                            changeTargetValue
                                                        }
                                                        value={
                                                            values.targetValue
                                                        }
                                                        data-gds-input=""
                                                        min="0"
                                                        step="0.1"
                                                    />

                                                    <span className="-m-l-2">
                                                        {metricValueString}
                                                    </span>
                                                </div>

                                                {hasError('targetValue') && (
                                                    <FormGroupHint>
                                                        {errors.targetValue}
                                                    </FormGroupHint>
                                                )}
                                            </FormGroup>
                                            {shouldDisplayBroadcastTooltip && (
                                                <p className="gds-text--body-xs -color-tx-dan">
                                                    {BroadcastImpressionTooltip}
                                                </p>
                                            )}
                                        </Col>
                                    </Row>
                                </Flex>
                            </Col>
                        </Row>
                        <Row gutter={16} className="-m-t-2">
                            <Col md={2}>
                                <div
                                    data-test="title"
                                    className="pacing-step-number"
                                >
                                    4
                                </div>
                            </Col>
                            <Col md={22}>
                                <Flex
                                    vertical
                                    className="gds-layout__container"
                                >
                                    <Row gutter={16}>
                                        <Col md={24}>
                                            <p>
                                                {t(
                                                    'pacing.createGoal.steps.details',
                                                )}
                                            </p>
                                        </Col>
                                        <Col md={12}>
                                            <div className="gds-flex__item gds-flex__item--grow-0">
                                                <FormGroup
                                                    layout="vertical"
                                                    disabled={isDisabled(
                                                        'name',
                                                    )}
                                                    context={
                                                        errors.name
                                                            ? 'danger'
                                                            : 'primary'
                                                    }
                                                >
                                                    <FormGroupLabel
                                                        text={t(
                                                            'pacing.createGoal.fields.goalName',
                                                        )}
                                                    />

                                                    <input
                                                        className="gds-form-group__text-input gds-form-group__text-input--sm"
                                                        type="text"
                                                        placeholder={t(
                                                            'pacing.createGoal.placeholders.goalName',
                                                        )}
                                                        required
                                                        value={values.name}
                                                        onChange={changeName}
                                                    />

                                                    {errors.name && (
                                                        <FormGroupHint>
                                                            {errors.name}
                                                        </FormGroupHint>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </Col>
                                    </Row>
                                </Flex>
                            </Col>
                        </Row>
                    </Flex>
                </Row>
                <Row gutter={16} className="wizard-pagination -m-t-3">
                    <Col md={24}>
                        <Button
                            size="sm"
                            context="secondary"
                            className="-inline-block -float-right"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {t('pacing.buttons.saveGoal')}
                        </Button>
                        <Button
                            className="gds-button gds-button--sm -inline-block -float-right -m-r-3 -color-bg-white gds-button--default"
                            onClick={navigatePacing}
                        >
                            {t('pacing.buttons.returnToPacing')}
                        </Button>
                    </Col>
                </Row>
            </Form>
            {isOpenHelperModal && (
                <TargetHelperModal
                    onClose={closeHelperModal}
                    setValue={set('targetValue')}
                    metric={values.metric}
                    rightsholder={values.rightsholder}
                    sponsor={values.sponsor}
                    placements={values.placements}
                    areAllPlacementsSelected={areAllPlacementsSelected}
                />
            )}
        </>
    );
};

export default connect(['client', 'filters'])(CreatePacingForm);
